import { cn } from "cn";
import { useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button/button";
import { CircularProgressbar } from "@/components/ui/circular-progressbar";
import {
  BwCloseLine,
  BwRefreshLoadLeftLine,
  BwWarningCircleFill,
} from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ATTACHMENT_DRAG_SOURCE_CLASS_NAME,
  getAttachmentsDragSourceProps,
} from "@/libs/attachment-drag";
import { getBytes } from "@/libs/file-helper";
import type { AttachmentSource } from "@/libs/form-attachment";
import { AttachmentFileName } from "./attachment-file-name";
import { type AttachmentAction, AttachmentMenu } from "./attachment-menu";
import { OVERLAY_BUTTON_CLASS_NAME } from "./attachment-overlay-button";
import { AttachmentPlayMark } from "./attachment-play-mark";
import {
  type AttachmentFile,
  resolveAttachmentPreviewKind,
} from "./attachment-preview-kind";
import { attachmentStateBackground } from "./attachment-state-background";
import { AttachmentTileCheckbox } from "./attachment-tile-checkbox";
import { AttachmentTypeIcon } from "./attachment-type-icon";
import {
  type AttachmentUploadStatus,
  describeUploadStage,
} from "./attachment-upload-stage";
import { AttachmentUploaderAvatar } from "./attachment-uploader-avatar";
import { usePdfFirstPage } from "./pdf-first-page";

/** The two shapes a preview is drawn at: 1:1, or 16:9 for a lone media in a message. */
export type AttachmentRatio = "square" | "video";

const RATIO_CLASS_NAME: Record<AttachmentRatio, string> = {
  square: "aspect-square",
  video: "aspect-video",
};

export type AttachmentUploader = { name: string; avatarUrl?: string | null };

/**
 * How a file that is not an image or video is drawn: `page` — the design's page card
 * with the type icon; `details` — the type icon, name and size (or upload stage) inside
 * a grey tile, which is how the upload strips name the documents being attached.
 */
export type AttachmentFileLayout = "page" | "details";

export type AttachmentProps = AttachmentFile & {
  ratio?: AttachmentRatio;
  fileLayout?: AttachmentFileLayout;
  /** Bytes — shown under the name by the `details` file layout. */
  fileSize?: number | null;
  /** Caption line 1: the name, truncating before its extension. */
  showFileName?: boolean;
  /** Caption line 2. Replaced by the upload stage while a file is in flight. */
  timestamp?: React.ReactNode;
  /** The top-left checkbox renders when `onSelectedChange` is passed. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** The top-right "…" menu. */
  actions?: AttachmentAction[];
  /** Puts a Remove button where the "…" menu would be — for files being attached. */
  onRemove?: () => void;
  /** The ringed avatar bottom-right. */
  uploader?: AttachmentUploader;
  status?: AttachmentUploadStatus;
  percent?: number;
  /** Offered on a failed upload while the uploader still holds the bytes. */
  onRetry?: () => void;
  /** A placeholder in the same shape, before the file is known. */
  loading?: boolean;
  /** Opens the file — a lightbox, typically. */
  onClick?: () => void;
  /** The picture did not load: a caller with another address for it points `thumbnailUri` there, and it is tried. */
  onPictureError?: () => void;
  /** Present ⇒ the picture and avatar (not the controls or caption) can be dragged into a composer, carrying these files. */
  dragAttachments?: AttachmentSource[];
  /** The root. Its width is the tile's width; the preview's height follows it. */
  className?: string;
  /** The preview box — e.g. a smaller corner radius. */
  previewClassName?: string;
};

/**
 * One attachment (design `MediaPreviewSmall` 9519:40644, with the `CardFileSmall`
 * 9519:41636 caption). It never sizes itself: it fills the width it is given — a grid
 * column, or a `w-*` class from a strip — and the preview keeps its ratio at any width.
 */
export function Attachment({
  fileName,
  originalContentUri,
  thumbnailUri,
  contentType,
  ratio = "square",
  fileLayout = "page",
  fileSize,
  showFileName = false,
  timestamp,
  selected = false,
  onSelectedChange,
  actions,
  onRemove,
  uploader,
  status,
  percent = 0,
  onRetry,
  loading = false,
  onClick,
  onPictureError,
  dragAttachments,
  className,
  previewClassName,
}: AttachmentProps) {
  const hasCaption = showFileName || timestamp != null;

  if (loading) {
    return (
      <div
        className={cn(
          /* sorted */
          "flex",
          "flex-col gap-0.5",
          "w-full min-w-0",
          className,
        )}
      >
        <Skeleton
          className={cn(
            /* sorted */
            "w-full",
            "rounded-2xl",
            RATIO_CLASS_NAME[ratio],
            previewClassName,
          )}
        />
        {hasCaption && (
          <div className="flex flex-col items-center gap-1 p-2">
            <Skeleton className="w-3/4 h-4" />
            <Skeleton className="w-1/2 h-3" />
          </div>
        )}
      </div>
    );
  }

  const inFlight = Boolean(status && status !== "done");
  const stage = describeUploadStage(status, percent);
  const file = { fileName, originalContentUri, thumbnailUri, contentType };
  const isDetailsTile =
    fileLayout === "details" &&
    resolveAttachmentPreviewKind(file).kind !== "picture";
  const preview = isDetailsTile ? (
    <AttachmentFileDetails
      fileName={fileName}
      contentType={contentType}
      fileSize={fileSize}
      status={status}
      stage={stage}
    />
  ) : (
    <AttachmentPicture
      file={file}
      hasError={status === "error"}
      onError={onPictureError}
    />
  );

  return (
    <div
      data-test="attachment-item"
      data-status={status ?? "done"}
      title={hasCaption ? undefined : fileName}
      className={cn(
        /* sorted */
        "flex",
        "flex-col gap-0.5",
        "w-full min-w-0",
        "group/attachment",
        className,
      )}
    >
      <div
        className={cn(
          /* sorted */
          "relative overflow-hidden",
          "w-full",
          "rounded-2xl",
          RATIO_CLASS_NAME[ratio],
          isDetailsTile &&
            "bg-bg-dark-min border border-border-dark-low shadow-sm hover:bg-bg-dark-low",
          isDetailsTile && status === "error" && "border-border-negative-max",
          !isDetailsTile &&
            status === "error" &&
            "ring-1 ring-border-negative-max ring-inset",
          attachmentStateBackground(selected, "group"),
          previewClassName,
        )}
      >
        {onClick && (
          <button
            type="button"
            aria-label={`Open ${fileName}`}
            onClick={onClick}
            className="absolute inset-0 rounded-[inherit] cursor-pointer"
          />
        )}

        {/* The picture and avatar, over the open button. A drag source takes the pointer itself — Firefox starts no drag inside a <button> — and is the drag ghost, without the checkbox and menu; otherwise clicks fall through to the button. */}
        <div
          {...(dragAttachments && {
            ...getAttachmentsDragSourceProps(dragAttachments),
            onClick,
          })}
          className={cn(
            /* sorted */
            "absolute inset-0 overflow-hidden",
            "rounded-[inherit]",
            dragAttachments
              ? ATTACHMENT_DRAG_SOURCE_CLASS_NAME
              : "pointer-events-none",
          )}
        >
          {preview}
          {uploader && (
            <AttachmentUploaderAvatar
              uploader={uploader}
              className="absolute right-2 bottom-2 pointer-events-auto"
            />
          )}
        </div>

        {/* Above the open button, so an in-flight file cannot be opened; below the
            checkbox and Remove button, so they stay clickable while it uploads or fails. */}
        {inFlight && (
          <AttachmentUploadOverlay
            status={status}
            percent={percent}
            onRetry={onRetry}
            overPicture={!isDetailsTile}
          />
        )}

        {onSelectedChange && (
          <AttachmentTileCheckbox
            fileName={fileName}
            selected={selected}
            onSelectedChange={onSelectedChange}
          />
        )}

        {onRemove ? (
          <Button
            kind="outline"
            variant="neutral"
            size="icon-xs"
            aria-label={`Remove ${fileName}`}
            data-test="attachment-item-delete-button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            className={cn(
              /* sorted */
              "absolute top-1 right-1",
              "pointer-fine:opacity-0",
              "transition-opacity",
              "group-hover/attachment:opacity-100 focus-visible:opacity-100",
              OVERLAY_BUTTON_CLASS_NAME,
            )}
          >
            <BwCloseLine size={14} />
          </Button>
        ) : (
          actions &&
          actions.length > 0 && (
            <AttachmentMenu
              fileName={fileName}
              actions={actions}
              className="absolute top-1 right-1"
            />
          )
        )}
      </div>

      {hasCaption && (
        // The preview button is the keyboard target; this only widens the pointer
        // target to the caption.
        <div
          onClick={onClick}
          className={cn(
            /* sorted */
            "flex",
            "flex-col items-center",
            "p-2",
            "rounded-2xl",
            "transition-colors",
            attachmentStateBackground(selected, "group"),
            onClick && "cursor-pointer",
          )}
        >
          {showFileName && (
            <AttachmentFileName
              fileName={fileName}
              className="justify-center w-full"
            />
          )}
          {(stage ?? timestamp) != null && (
            <span
              className={cn(
                /* sorted */
                "w-full",
                "text-xs text-center truncate",
                status === "error"
                  ? "text-text-negative"
                  : "text-text-dark-high",
              )}
            >
              {stage ?? timestamp}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A document in an upload strip: its type icon at the top, its name and — while it is
 * in flight — the upload stage, or else its size, at the bottom.
 */
function AttachmentFileDetails({
  fileName,
  contentType,
  fileSize,
  status,
  stage,
}: {
  fileName: string;
  contentType?: string | null;
  fileSize?: number | null;
  status?: AttachmentUploadStatus;
  stage: string | null;
}) {
  return (
    <span
      className={cn(
        /* sorted */
        "flex",
        "flex-col justify-between",
        "size-full",
        "p-2",
        "text-left",
      )}
    >
      <AttachmentTypeIcon
        fileName={fileName}
        contentType={contentType}
        className="size-8 shrink-0 rounded-lg"
      />
      <span className="block mt-auto">
        <span className="block w-full text-sm text-text-dark-max truncate">
          {fileName}
        </span>
        {/* An unknown size (a size the pipeline never wrote back) is not a zero-byte
            file — the name stands alone rather than claiming "0 Bytes". */}
        {stage ? (
          <span
            className={cn(
              /* sorted */
              "block",
              "text-[9px]",
              status === "error" ? "text-text-negative" : "text-text-dark-low",
            )}
          >
            {stage}
          </span>
        ) : (
          fileSize != null &&
          fileSize > 0 && (
            <span className="block text-xs text-text-dark-low">
              {getBytes(fileSize)}
            </span>
          )
        )}
      </span>
    </span>
  );
}

/**
 * The picture filling the preview: an image or a video's poster (with a play mark), a
 * document's first page on a page card, or — when there is nothing to paint, or the
 * browser cannot decode it (HEIC, TIFF) — the type icon on a blank page card.
 */
function AttachmentPicture({
  file,
  hasError,
  onError,
}: {
  file: AttachmentFile;
  hasError: boolean;
  onError?: () => void;
}) {
  const preview = resolveAttachmentPreviewKind(file);
  const src = preview.kind === "icon" ? null : preview.src;
  const [failed, setFailed] = useState(false);
  // A new src deserves a fresh attempt — an upload's tile is re-pointed from the local
  // preview to the CDN rendition, and a stale failure would suppress the good one.
  useEffect(() => setFailed(false), [src]);

  if (preview.kind === "picture" && !failed) {
    return (
      <span className="relative block size-full bg-bg-dark-min rounded-[inherit]">
        <img
          src={preview.src}
          alt={file.fileName}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="object-cover size-full"
          onError={() => {
            setFailed(true);
            onError?.();
          }}
        />
        {preview.isVideo && <AttachmentPlayMark />}
        {/* Drawn over the picture, not on its box — a full-bleed image would paint over
            a border or inset ring there. It keeps a white or black picture from melting
            into the page around it, and turns red on a failed upload. */}
        <span
          className={cn(
            /* sorted */
            "absolute inset-0",
            "border rounded-[inherit] pointer-events-none",
            hasError ? "border-border-negative-max" : "border-border-dark-low",
          )}
        />
      </span>
    );
  }

  if (preview.kind === "pdf") {
    return <AttachmentPdfPage file={file} src={preview.src} />;
  }

  return (
    <AttachmentPageCard
      file={file}
      pageSrc={preview.kind === "page" && !failed ? preview.src : null}
      onPageError={() => setFailed(true)}
    />
  );
}

/**
 * A PDF with no rendition: its first page is rendered in the browser once the tile
 * nears the viewport. Until then — and for good if it cannot be read — the card shows
 * the type icon, exactly as a PDF did before.
 */
function AttachmentPdfPage({
  file,
  src,
}: {
  file: AttachmentFile;
  src: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "200px" });
  const page = usePdfFirstPage(src, inView);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const pageSrc =
    page.status === "ready" && page.url !== failedUrl ? page.url : null;

  return (
    <AttachmentPageCard
      ref={ref}
      file={file}
      pageSrc={pageSrc}
      onPageError={() => setFailedUrl(pageSrc)}
    />
  );
}

/**
 * A sheet of paper: the document's first page when there is one to paint, else the type
 * icon on a blank card.
 */
function AttachmentPageCard({
  ref,
  file,
  pageSrc,
  onPageError,
}: {
  ref?: React.Ref<HTMLSpanElement>;
  file: AttachmentFile;
  pageSrc: string | null;
  onPageError: () => void;
}) {
  return (
    // 89 % of the preview's height at a portrait page's proportions, so it reads as a
    // sheet of paper at either ratio.
    <span
      ref={ref}
      className={cn(
        /* sorted */
        "absolute top-1/2 left-1/2 flex overflow-hidden",
        "items-center justify-center",
        "h-[89%] aspect-[61/89]",
        "border rounded-sm shadow-sm",
        "-translate-x-1/2 -translate-y-1/2",
        pageSrc
          ? "border-border-dark-min"
          : "bg-bg-surface-elevate-low border-border-dark-low",
      )}
    >
      {pageSrc ? (
        <img
          src={pageSrc}
          alt={file.fileName}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="object-cover size-full"
          onError={onPageError}
        />
      ) : (
        <AttachmentTypeIcon
          fileName={file.fileName}
          contentType={file.contentType}
          size={24}
          className="w-4/5 max-w-6 h-auto"
        />
      )}
    </span>
  );
}

function AttachmentUploadOverlay({
  status,
  percent,
  onRetry,
  overPicture,
}: {
  status?: AttachmentUploadStatus;
  percent: number;
  onRetry?: () => void;
  /** A picture needs a scrim for the ring to read, and says "Failed" itself; a details tile has its own caption line. */
  overPicture: boolean;
}) {
  return (
    <div
      data-test="attachment-item-progress"
      className={cn(
        /* sorted */
        "absolute inset-0 flex",
        "flex-col items-center justify-center gap-1",
        overPicture && "bg-bg-dark-min/50 backdrop-blur-[1px]",
      )}
    >
      {status === "error" ? (
        <>
          {onRetry ? (
            <Button
              kind="solid"
              variant="neutral"
              size="icon-sm"
              aria-label="Retry upload"
              className="rounded-full"
              onClick={(event) => {
                event.stopPropagation();
                onRetry();
              }}
            >
              <BwRefreshLoadLeftLine size={16} />
            </Button>
          ) : (
            <BwWarningCircleFill className="text-text-negative" size={20} />
          )}
          {overPicture && (
            <span className="text-xs text-text-negative font-medium">
              {onRetry ? "Failed — retry" : "Failed"}
            </span>
          )}
        </>
      ) : (
        <CircularProgressbar
          value={percent}
          className="w-6 h-6 text-text-primary"
        />
      )}
    </div>
  );
}
